import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';
import {
  Gift, Search, X, CheckSquare, CheckCircle, DollarSign,
  Plus, Loader2
} from 'lucide-react';

interface RewardRow {
  id: number;
  profileId: number;
  phone: string;
  name?: string;
  sourceOrderId: number;
  sourceOrderNum?: string;
  rewardType: string;
  amount: number;
  description?: string;
  status: string;
  createdAt: number;
}

interface Summary {
  totalPending: number;
  totalApproved: number;
  totalPaid: number;
}

const STATUS_BADGES: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-700',
};

const STATUS_LABELS: Record<string, string> = {
  pending: '待處理',
  approved: '已審核',
  paid: '已支付',
  cancelled: '已取消',
};

function formatCents(cents = 0): string {
  return `HK$${Math.round(cents / 100).toLocaleString('zh-HK')}`;
}

function formatDate(ts?: number): string {
  if (!ts) return '-';
  const d = new Date(ts * 1000);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('zh-HK');
}

const AdminReferralRewards: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();

  const [rewards, setRewards] = useState<RewardRow[]>([]);
  const [summary, setSummary] = useState<Summary>({ totalPending: 0, totalApproved: 0, totalPaid: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  const [statusFilter, setStatusFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const [showManualModal, setShowManualModal] = useState(false);
  const [manualPhone, setManualPhone] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [manualDesc, setManualDesc] = useState('');

  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, phoneFilter, typeFilter]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        });
        if (statusFilter) params.set('status', statusFilter);
        if (phoneFilter) params.set('phone', phoneFilter);
        if (typeFilter) params.set('rewardType', typeFilter);

        const res = await apiFetch('/api/public/admin/referral-rewards?' + params.toString());
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || '獲取獎勵列表失敗');
        }
        const json = await res.json();
        if (!cancelled) {
          setRewards(Array.isArray(json.data) ? json.data : []);
          setTotal(json.total || 0);
          setSummary(json.summary || { totalPending: 0, totalApproved: 0, totalPaid: 0 });
          setSelectedIds(new Set());
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message || '加載失敗');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [page, limit, statusFilter, phoneFilter, typeFilter]);

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (rewards.length > 0 && rewards.every((r) => selectedIds.has(r.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rewards.map((r) => r.id)));
    }
  };

  const handleApprove = async (id: number) => {
    if (!confirm('確認審核通過這筆獎勵？')) return;
    setActionLoading(true);
    try {
      const res = await apiFetch(`/api/public/admin/referral-rewards/${id}/approve`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess('審核通過');
        refresh();
      } else {
        setError(data.error || '審核失敗');
      }
    } catch (e: any) {
      setError(e.message || '審核失敗');
    } finally {
      setActionLoading(false);
      setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    }
  };

  const handleMarkPaid = async (id: number) => {
    if (!confirm('確認標記這筆獎勵為已支付？')) return;
    setActionLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/referral-rewards/bulk-pay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [id] }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess('已標記支付');
        refresh();
      } else {
        setError(data.error || '標記失敗');
      }
    } catch (e: any) {
      setError(e.message || '標記失敗');
    } finally {
      setActionLoading(false);
      setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    }
  };

  const handleBulkPay = async () => {
    const eligible = Array.from(selectedIds).filter((id) => {
      const row = rewards.find((r) => r.id === id);
      return row?.status === 'approved';
    });
    if (eligible.length === 0) {
      setError('請選擇至少一條「已審核」狀態的記錄');
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!confirm(`確認批量標記 ${eligible.length} 筆獎勵為已支付？`)) return;
    setActionLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/referral-rewards/bulk-pay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: eligible }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess(`已成功標記 ${data.count || eligible.length} 筆為已支付`);
        refresh();
      } else {
        setError(data.error || '批量支付失敗');
      }
    } catch (e: any) {
      setError(e.message || '批量支付失敗');
    } finally {
      setActionLoading(false);
      setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    }
  };

  const refresh = () => {
    // Trigger re-fetch by toggling a dep or calling directly. Use current state refetch.
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (statusFilter) params.set('status', statusFilter);
    if (phoneFilter) params.set('phone', phoneFilter);
    if (typeFilter) params.set('rewardType', typeFilter);

    apiFetch('/api/public/admin/referral-rewards?' + params.toString())
      .then(async (res) => {
        if (res.ok) {
          const json = await res.json();
          setRewards(Array.isArray(json.data) ? json.data : []);
          setTotal(json.total || 0);
          setSummary(json.summary || { totalPending: 0, totalApproved: 0, totalPaid: 0 });
          setSelectedIds(new Set());
        }
      })
      .catch(() => {});
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountNum = parseFloat(manualAmount);
    if (!manualPhone || !amountNum || amountNum <= 0) {
      setError('請填寫有效的電話和正數金額');
      return;
    }
    const amountCents = Math.round(amountNum * 100);
    setActionLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/referral-rewards/manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: manualPhone.trim(), amount: amountCents, description: manualDesc.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess('手動獎勵已添加');
        setShowManualModal(false);
        setManualPhone('');
        setManualAmount('');
        setManualDesc('');
        refresh();
      } else {
        setError(data.error || '添加失敗');
      }
    } catch (err: any) {
      setError(err.message || '添加失敗');
    } finally {
      setActionLoading(false);
      setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    }
  };

  const eligibleSelectedCount = Array.from(selectedIds).filter((id) =>
    rewards.find((r) => r.id === id)?.status === 'approved'
  ).length;

  return (
    <AdminLayout currentPage="referral-rewards" onLogout={handleLogout}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Gift className="w-6 h-6 text-orange-600" />
          推薦獎勵管理
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">審核與支付推薦獎勵</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100"><X className="w-5 h-5" /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100"><X className="w-5 h-5" /></button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">待支付總額</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white">{formatCents(summary.totalPending)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">已審核總額</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white">{formatCents(summary.totalApproved)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">已支付總額</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white">{formatCents(summary.totalPaid)}</div>
        </div>
      </div>

      {/* Filters + Actions */}
      <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 flex-1">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">狀態</label>
              <select
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">全部狀態</option>
                <option value="pending">待處理</option>
                <option value="approved">已審核</option>
                <option value="paid">已支付</option>
                <option value="cancelled">已取消</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">推薦人電話</label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="852..."
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
                  value={phoneFilter}
                  onChange={(e) => setPhoneFilter(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">獎勵類型</label>
              <select
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">全部類型</option>
                <option value="direct">direct</option>
                <option value="indirect">indirect</option>
                <option value="manual">manual</option>
              </select>
            </div>
          </div>
          {can(PERMISSIONS.REFERRALS_WRITE) && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkPay}
                disabled={eligibleSelectedCount === 0 || actionLoading}
                className="inline-flex items-center gap-1 bg-green-600 text-white px-4 py-2.5 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckSquare className="w-4 h-4" />}
                批量標記已支付 {eligibleSelectedCount > 0 && `(${eligibleSelectedCount})`}
              </button>
              <button
                onClick={() => setShowManualModal(true)}
                className="inline-flex items-center gap-1 bg-orange-600 text-white px-4 py-2.5 rounded-lg text-sm hover:bg-orange-700 transition-colors"
              >
                <Plus className="w-4 h-4" /> 手動添加
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              <tr>
                <th className="p-4 w-10">
                  <input
                    type="checkbox"
                    checked={rewards.length > 0 && rewards.every((r) => selectedIds.has(r.id))}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-orange-600 rounded"
                  />
                </th>
                <th className="p-4">推薦人</th>
                <th className="p-4">來源訂單</th>
                <th className="p-4">類型</th>
                <th className="p-4 text-right">金額</th>
                <th className="p-4">描述</th>
                <th className="p-4">狀態</th>
                <th className="p-4">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center text-gray-400">加載中...</td></tr>
              ) : rewards.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-gray-500 dark:text-gray-400">暫無獎勵記錄</td></tr>
              ) : (
                rewards.map((r) => (
                  <tr key={r.id} className="hover:bg-orange-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="p-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        className="w-4 h-4 text-orange-600 rounded"
                      />
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-gray-800 dark:text-white">{r.name || '匿名'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{r.phone}</div>
                    </td>
                    <td className="p-4 font-mono text-gray-600 dark:text-gray-300">{r.sourceOrderNum || r.sourceOrderId || '-'}</td>
                    <td className="p-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 capitalize">{r.rewardType}</span>
                    </td>
                    <td className="p-4 text-right font-medium text-gray-800 dark:text-white">{formatCents(r.amount)}</td>
                    <td className="p-4 text-gray-600 dark:text-gray-300 max-w-xs truncate">{r.description || '-'}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGES[r.status] || 'bg-gray-100 text-gray-700'}`}>
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </td>
                    <td className="p-4 space-x-2 whitespace-nowrap">
                      {can(PERMISSIONS.REFERRALS_WRITE) && r.status === 'pending' && (
                        <button
                          onClick={() => handleApprove(r.id)}
                          disabled={actionLoading}
                          className="inline-flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                          <CheckCircle size={12} /> 審核通過
                        </button>
                      )}
                      {can(PERMISSIONS.REFERRALS_WRITE) && r.status === 'approved' && (
                        <button
                          onClick={() => handleMarkPaid(r.id)}
                          disabled={actionLoading}
                          className="inline-flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded text-xs hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          <DollarSign size={12} /> 標記已支付
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-center items-center gap-3">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              上一頁
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">第 {page} / {totalPages} 頁</span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              下一頁
            </button>
          </div>
        )}
      </div>

      {/* Manual Modal */}
      {showManualModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">手動添加獎勵</h2>
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">推薦人電話</label>
                <input
                  type="text"
                  required
                  value={manualPhone}
                  onChange={(e) => setManualPhone(e.target.value)}
                  placeholder="85291234567"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">金額（HK$）</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  placeholder="100"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">輸入港幣金額，系統會自動換算為分</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">描述</label>
                <textarea
                  rows={3}
                  value={manualDesc}
                  onChange={(e) => setManualDesc(e.target.value)}
                  placeholder="例如：特別活動獎勵"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white resize-none"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowManualModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  取消
                </button>
                {can(PERMISSIONS.REFERRALS_WRITE) && (
                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-1"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : '確認添加'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminReferralRewards;
